import type { ReactElement } from "react"
import styled from "styled-components"
import { type AccessBadgeTone, accessBadge } from "../accessBadge"
import type { AccessWindow } from "../types"

const toneStyles: Record<AccessBadgeTone, { readonly color: string; readonly surface: string }> = {
  expired: { color: "error", surface: "errorSurface" },
  upcoming: { color: "link", surface: "white" },
  ending: { color: "textPrimary", surface: "white" },
}

const Pill = styled.span<{ $tone: AccessBadgeTone }>`
  padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xs}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /** Over artwork, so it carries its own ground rather than borrowing the card's. */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  color: ${({ theme, $tone }) => theme.color[toneStyles[$tone].color as keyof typeof theme.color]};
  background-color: ${({ theme, $tone }) => theme.color[toneStyles[$tone].surface as keyof typeof theme.color]};
`

interface AccessBadgeProps {
  readonly accessWindow: AccessWindow | undefined
  readonly qaHook: string
}

/** The window as one pill over the artwork. Null when there is nothing worth saying. */
export const AccessBadge = ({ accessWindow, qaHook }: AccessBadgeProps): ReactElement | null => {
  const badge = accessBadge(accessWindow)

  if (!badge) {
    return null
  }

  return (
    <Pill $tone={badge.tone} title={badge.detail} data-qa-hook={`${qaHook}-${badge.tone}`}>
      {badge.label}
    </Pill>
  )
}
